import React, { useEffect, useState } from "react";
import { backend, blogurl, categoryurl } from "../../../../path";
import LoaderMain from "../../../layout/header/loader_sub";
import { Link } from "react-router-dom";
import styled, { keyframes } from "styled-components";
import { FiSearch, FiX, FiUser, FiArrowRight } from "react-icons/fi";
import SEOHead from "../../../components/SEOHead";

const SEO_BLOG_POSTS = [
  {
    blog_id: 101,
    title: "Complete Guide to Buying Authentic Balaramapuram Elastic Kasavu Mundu",
    short_description: "Discover the heritage of Balaramapuram weaving, handloom gold zari craftsmanship, and modern elastic Kasavu Mundu wearing tips for Onam, Vishu, and weddings.",
    image: "banner.png",
    author: "KeralaSpecial Heritage Team",
    created_at: "2026-07-28"
  },
  {
    blog_id: 102,
    title: "Top 10 Health Benefits of Authentic Organic Kerala Spices",
    short_description: "Learn why Wayanad black pepper, Idukki green cardamom, and Kerala organic turmeric are world-renowned for aroma, flavor, and medicinal wellness.",
    image: "banner1.png",
    author: "Spice Specialist",
    created_at: "2026-07-20"
  }
];

function BlogUser(props) {
  const [data, setData] = useState([]);
  const [catData, setCatData] = useState([]);
  const [loading, setLoading] = useState();
  const [searchTerm, setSearchTerm] = useState("");

  const fetchData = () => {
    setLoading(true);
    fetch(backend + "getblog", {
      method: "GET",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then((resData) => {
        if (Array.isArray(resData) && resData.length > 0) {
          setData([...resData, ...SEO_BLOG_POSTS]);
        } else {
          setData(SEO_BLOG_POSTS);
        }
      })
      .catch((error) => {
        console.error(error);
        setData(SEO_BLOG_POSTS);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchCatData = () => {
    setLoading(true);
    fetch(backend + "getcategorywithsubcategory", {
      method: "GET",
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        setCatData(data);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
    fetchCatData();
    window.scrollTo(0, 0);
  }, []);

  // Filter blogs locally based on search term
  const filteredBlogs = data.filter((blog) => {
    if (!blog) return false;
    const term = searchTerm.toLowerCase();
    const titleMatch = blog.title?.toLowerCase().includes(term);
    const contentMatch = blog.content?.toLowerCase().includes(term);
    return titleMatch || contentMatch;
  });

  // Extract featured post (newest post) and rest of the posts
  const featuredBlog = filteredBlogs.length > 0 ? filteredBlogs[0] : null;
  const regularBlogs = filteredBlogs.length > 1 ? filteredBlogs.slice(1) : [];

  const handleTagClick = (tag) => {
    setSearchTerm(tag);
    // Also trigger global search if function is passed
    if (props?.setSearch) {
      props.setSearch(tag);
    }
  };

  // Helper to remove HTML tags for the excerpt
  const stripHtml = (html) => {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };

  return (
    <main className="main" id="main-section" style={{ backgroundColor: "#FDFBF7" }}>
      <SEOHead
        title="Kerala Culture, Spices, Handlooms & Shopping Guides | Blog | KeralaSpecial.in"
        description="Read in-depth guides, articles, and insights on authentic Kerala Kasavu Mundu, organic Wayanad spices, Ayurvedic remedies, handicraft heritage, and traditional festivals."
        keywords="Kerala Blog, Kasavu Mundu Guide, Kerala Spices Benefits, Balaramapuram Handloom Story, Ayurvedic Living Kerala, Kerala Festival Shopping"
        canonical="https://keralaspecialtours.com/blog"
        schema={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://keralaspecialtours.com/" },
            { "@type": "ListItem", "position": 2, "name": "Blog", "item": "https://keralaspecialtours.com/blog" }
          ]
        }}
      />
      <div className="page-header breadcrumb-wrap" style={{ backgroundColor: "#F2ECE1", borderBottom: "1px solid rgba(197, 168, 128, 0.2)" }}>
        <div className="container">
          <div className="breadcrumb" style={{ fontFamily: "Jost, sans-serif", fontSize: "14px" }}>
            <a href="/" className="breadcrumb-item d-inline-block text-kerala-green" title="Home" style={{ color: "#123524", fontWeight: "500" }}>
              Home
            </a>
            <span style={{ margin: "0 8px", color: "#C5A880" }}>/</span>
            <div className="breadcrumb-item d-inline-block active" style={{ color: "#2D1F18", fontWeight: "600" }}>Blogs</div>
          </div>
        </div>
      </div>

      <PageWrapper>
        <HeroSection>
          <HeroContent>
            <HeroBadge>Stories from Kerala</HeroBadge>
            <HeroTitle>The Kerala Specials Blog</HeroTitle>
            <HeroSubtitle>
              Artisan Journeys, Cultural Tales & Heritage Stories
            </HeroSubtitle>
            <Paragraph>
              Immerse yourself in inspiring stories of master weavers, the heritage of single-origin spices, the wellness secrets of Ayurveda, and deep cultural traditions straight from the heart of Kerala.
            </Paragraph>
            <HeroStats>
              <StatCard>
                <StatValue>{data.length || 0}</StatValue>
                <StatLabel>Articles</StatLabel>
              </StatCard>
              <StatCard>
                <StatValue>{catData.length || 0}</StatValue>
                <StatLabel>Categories</StatLabel>
              </StatCard>
            </HeroStats>
          </HeroContent>
          <HeroVisual>
            <HeroImage src="/storage/general/about.png" alt="Blog showcase" />
            <HeroOverlayCard>
              <OverlayTitle>Living Heritage</OverlayTitle>
              <OverlaySubtitle>Sustaining traditional livelihoods and craft communities.</OverlaySubtitle>
            </HeroOverlayCard>
          </HeroVisual>
        </HeroSection>

        {/* Local Search Bar */}
        <SearchFilterBar>
          <SearchWrapper>
            <FiSearch className="search-icon" />
            <SearchInput
              type="text"
              placeholder="Search stories, crafts, spices..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <ClearButton onClick={() => setSearchTerm("")}>
                <FiX />
              </ClearButton>
            )}
          </SearchWrapper>
          {searchTerm && (
            <FilterInfo>
              Showing {filteredBlogs.length} results for "{searchTerm}"
            </FilterInfo>
          )}
        </SearchFilterBar>

        <MainContent>
          <BlogSection>
            {filteredBlogs.length === 0 ? (
              <NoResultsCard>
                <h3>No Stories Found</h3>
                <p>We couldn't find any articles matching "{searchTerm}". Try checking your spelling or looking for other topics.</p>
                <ResetButton onClick={() => setSearchTerm("")}>View All Stories</ResetButton>
              </NoResultsCard>
            ) : (
              <>
                {/* Featured Story Layout (Only when search term matches and we have a featured blog) */}
                {featuredBlog && !searchTerm && (
                  <FeaturedStoryCard>
                    <FeaturedThumbnail>
                      <Link to={`/blog/${featuredBlog.blog_id}`}>
                        <img src={blogurl + featuredBlog.thumbnail} alt={featuredBlog.title} />
                      </Link>
                    </FeaturedThumbnail>
                    <FeaturedContent>
                      <FeaturedBadge>Featured Story</FeaturedBadge>
                      <FeaturedTitle>
                        <Link to={`/blog/${featuredBlog.blog_id}`}>{featuredBlog.title}</Link>
                      </FeaturedTitle>
                      <FeaturedExcerpt>
                        {stripHtml(featuredBlog.content).slice(0, 160)}...
                      </FeaturedExcerpt>
                      <FeaturedFooter>
                        <MetaInfo>
                          <FiUser /> {featuredBlog.author || "KeralaSpecial Team"}
                        </MetaInfo>
                        <ReadMoreLink to={`/blog/${featuredBlog.blog_id}`}>
                          Read Article <FiArrowRight />
                        </ReadMoreLink>
                      </FeaturedFooter>
                    </FeaturedContent>
                  </FeaturedStoryCard>
                )}

                {/* Grid Header */}
                <SectionHeader>
                  <SectionTitle>{searchTerm ? "Search Results" : "Recent Articles"}</SectionTitle>
                  <div className="section-divider"></div>
                </SectionHeader>

                {/* Regular Blog Grid */}
                <BlogGrid>
                  {/* If searching, render all matching. Otherwise render regular blogs (excl. first one) */}
                  {(searchTerm ? filteredBlogs : regularBlogs).map((blog, index) => (
                    <BlogCard key={blog.blog_id} delay={index * 0.05}>
                      <BlogThumbnail>
                        <Link to={`/blog/${blog.blog_id}`}>
                          <img src={blogurl + blog.thumbnail} alt={blog.title} />
                        </Link>
                      </BlogThumbnail>
                      <BlogContent>
                        <CardMeta>
                          <span className="author">By {blog.author || "KeralaSpecial Team"}</span>
                        </CardMeta>
                        <BlogTitle>
                          <Link to={`/blog/${blog.blog_id}`}>{blog.title}</Link>
                        </BlogTitle>
                        <CardExcerpt>
                          {stripHtml(blog.content).slice(0, 100)}...
                        </CardExcerpt>
                        <BlogFooter>
                          <ReadMoreLink to={`/blog/${blog.blog_id}`}>
                            Read Story <FiArrowRight />
                          </ReadMoreLink>
                        </BlogFooter>
                      </BlogContent>
                    </BlogCard>
                  ))}
                </BlogGrid>
              </>
            )}
          </BlogSection>

          <Sidebar>
            <SidebarCard>
              <SidebarHeading>Explore Categories</SidebarHeading>
              <CategoryList>
                {catData.map((item, i) => (
                  <CategoryItem key={i} delay={i * 0.04}>
                    <Link to={`/category/${item.category_slug}`}>
                      <div className="category-info">
                        <img
                          loading="lazy"
                          src={categoryurl + item.logo}
                          alt={item.category_name}
                        />
                        <span>{item.category_name}</span>
                      </div>
                      <Badge>{item.subcategory.length}</Badge>
                    </Link>
                  </CategoryItem>
                ))}
              </CategoryList>
            </SidebarCard>

            <SidebarCard>
              <SidebarHeading>Popular Tags</SidebarHeading>
              <TagList>
                {[
                  "Kannadi",
                  "Mundu",
                  "Amadapetti",
                  "Balaramapuram",
                  "Spices",
                  "Handicrafts",
                ].map((tag, i) => (
                  <TagItem key={tag} delay={i * 0.04}>
                    <TagButton type="button" onClick={() => handleTagClick(tag)} active={searchTerm === tag}>
                      #{tag}
                    </TagButton>
                  </TagItem>
                ))}
              </TagList>
            </SidebarCard>
          </Sidebar>
        </MainContent>

        {loading && <LoaderMain />}
      </PageWrapper>
    </main>
  );
}

export default BlogUser;

// --- STYLED COMPONENTS ---

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInRight = keyframes`
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const PageWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px 96px;
  display: flex;
  flex-direction: column;
  gap: 56px;
`;

const HeroSection = styled.section`
  margin-top: 48px;
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 48px;
  align-items: center;
  background: linear-gradient(135deg, #F2ECE1 0%, #FDFBF7 100%);
  padding: 48px;
  border-radius: 28px;
  border: 1px solid rgba(197, 168, 128, 0.25);
  box-shadow: 0 16px 36px rgba(45, 31, 24, 0.02);
  animation: ${fadeInUp} 0.6s ease forwards;

  @media screen and (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 32px;
    padding: 36px;
  }

  @media screen and (max-width: 640px) {
    padding: 24px;
    margin-top: 24px;
  }
`;

const HeroContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const HeroBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 6px 14px;
  border-radius: 999px;
  background: rgba(18, 53, 36, 0.08);
  color: #123524;
  font-family: "Jost", sans-serif;
  font-weight: 600;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  font-size: 11px;
  width: fit-content;
  border: 1px solid rgba(197, 168, 128, 0.25);
`;

const HeroTitle = styled.h1`
  margin: 0;
  font-family: "Playfair Display", serif;
  font-size: 42px;
  font-weight: 700;
  line-height: 1.2;
  color: #123524;

  @media screen and (max-width: 768px) {
    font-size: 32px;
  }
`;

const HeroSubtitle = styled.h3`
  margin: 0;
  font-family: "Jost", sans-serif;
  font-size: 18px;
  color: #C5A880;
  font-weight: 500;
  letter-spacing: 0.5px;
`;

const Paragraph = styled.p`
  margin: 0;
  font-family: "Poppins", sans-serif;
  color: #2D1F18;
  opacity: 0.85;
  line-height: 1.7;
  font-size: 15px;
`;

const HeroStats = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 8px;

  @media screen and (max-width: 480px) {
    flex-direction: row;
  }
`;

const StatCard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 16px 24px;
  border: 1px solid rgba(197, 168, 128, 0.2);
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 120px;
  box-shadow: 0 4px 12px rgba(45, 31, 24, 0.01);
`;

const StatValue = styled.span`
  font-family: "Playfair Display", serif;
  font-size: 28px;
  font-weight: 700;
  color: #123524;
`;

const StatLabel = styled.span`
  font-family: "Jost", sans-serif;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1.2px;
  color: #C5A880;
  font-weight: 600;
`;

const HeroVisual = styled.div`
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  height: 300px;
  box-shadow: 0 12px 24px rgba(45, 31, 24, 0.05);
  border: 1px solid rgba(197, 168, 128, 0.15);
`;

const HeroImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const HeroOverlayCard = styled.div`
  position: absolute;
  bottom: 16px;
  left: 16px;
  right: 16px;
  background: rgba(253, 251, 247, 0.95);
  border-radius: 14px;
  padding: 14px 18px;
  border: 1px solid rgba(197, 168, 128, 0.2);
  box-shadow: 0 8px 16px rgba(45, 31, 24, 0.08);
  backdrop-filter: blur(4px);
`;

const OverlayTitle = styled.h5`
  margin: 0;
  font-family: "Playfair Display", serif;
  font-size: 15px;
  color: #123524;
  font-weight: 700;
`;

const OverlaySubtitle = styled.p`
  margin: 4px 0 0;
  font-family: "Poppins", sans-serif;
  color: #2D1F18;
  opacity: 0.8;
  font-size: 12px;
  line-height: 1.4;
`;

const SearchFilterBar = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
  animation: ${fadeInUp} 0.6s ease forwards;
`;

const SearchWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 500px;
  display: flex;
  align-items: center;

  .search-icon {
    position: absolute;
    left: 16px;
    color: #C5A880;
    font-size: 18px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 12px 48px 12px 44px;
  border-radius: 30px;
  border: 1px solid rgba(197, 168, 128, 0.35);
  background: #ffffff;
  font-family: "Jost", sans-serif;
  font-size: 15px;
  color: #2D1F18;
  outline: none;
  transition: all 0.3s ease;

  &:focus {
    border-color: #123524;
    box-shadow: 0 4px 12px rgba(18, 53, 36, 0.05);
  }

  &::placeholder {
    color: #a0a0a0;
  }
`;

const ClearButton = styled.button`
  position: absolute;
  right: 16px;
  background: none;
  border: none;
  color: #a0a0a0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  padding: 4px;
  border-radius: 50%;
  transition: background 0.2s;

  &:hover {
    background: #f0f0f0;
    color: #2d1f18;
  }
`;

const FilterInfo = styled.span`
  font-family: "Jost", sans-serif;
  font-size: 13px;
  color: #C5A880;
  font-weight: 500;
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 1.7fr 1.0fr;
  gap: 40px;
  animation: ${fadeInUp} 0.7s ease forwards;

  @media screen and (max-width: 992px) {
    grid-template-columns: 1fr;
    gap: 48px;
  }
`;

const BlogSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const NoResultsCard = styled.div`
  text-align: center;
  padding: 64px 32px;
  background: #ffffff;
  border-radius: 20px;
  border: 1px dashed rgba(197, 168, 128, 0.4);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  h3 {
    font-family: "Playfair Display", serif;
    color: #123524;
    margin: 0;
    font-size: 24px;
  }

  p {
    font-family: "Poppins", sans-serif;
    color: #2D1F18;
    opacity: 0.7;
    max-width: 400px;
    margin: 0;
    font-size: 14px;
    line-height: 1.6;
  }
`;

const ResetButton = styled.button`
  margin-top: 8px;
  background: #123524;
  color: #FDFBF7;
  border: none;
  border-radius: 30px;
  padding: 10px 24px;
  font-family: "Jost", sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 4px 12px rgba(18, 53, 36, 0.15);

  &:hover {
    background: #C5A880;
    transform: translateY(-2px);
  }
`;

const FeaturedStoryCard = styled.article`
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  background: #ffffff;
  border-radius: 24px;
  overflow: hidden;
  border: 1px solid rgba(197, 168, 128, 0.2);
  box-shadow: 0 12px 30px rgba(45, 31, 24, 0.02);
  transition: all 0.4s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 45px rgba(45, 31, 24, 0.06);
    border-color: rgba(197, 168, 128, 0.4);
  }

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FeaturedThumbnail = styled.div`
  width: 100%;
  height: 320px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.6s ease;
  }

  ${FeaturedStoryCard}:hover & img {
    transform: scale(1.03);
  }
`;

const FeaturedContent = styled.div`
  padding: 32px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 14px;
`;

const FeaturedBadge = styled.span`
  color: #C5A880;
  font-family: "Jost", sans-serif;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1.5px;
`;

const FeaturedTitle = styled.h2`
  margin: 0;
  font-family: "Playfair Display", serif;
  font-size: 26px;
  font-weight: 700;
  line-height: 1.3;

  a {
    color: #123524;
    transition: color 0.3s;

    &:hover {
      color: #C5A880;
    }
  }
`;

const FeaturedExcerpt = styled.p`
  margin: 0;
  font-family: "Poppins", sans-serif;
  color: #2D1F18;
  opacity: 0.8;
  font-size: 14px;
  line-height: 1.6;
`;

const FeaturedFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
`;

const MetaInfo = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: "Jost", sans-serif;
  font-size: 13px;
  color: #888888;
`;

const ReadMoreLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: "Jost", sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #123524;
  transition: all 0.3s;

  svg {
    transition: transform 0.2s;
  }

  &:hover {
    color: #C5A880;
    
    svg {
      transform: translateX(4px);
    }
  }
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;
`;

const SectionTitle = styled.h2`
  margin: 0;
  font-family: "Playfair Display", serif;
  font-size: 24px;
  font-weight: 700;
  color: #123524;
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 28px;

  @media screen and (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const BlogCard = styled.article`
  background: #ffffff;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid rgba(197, 168, 128, 0.18);
  box-shadow: 0 8px 20px rgba(45, 31, 24, 0.015);
  display: flex;
  flex-direction: column;
  transform: translateY(15px);
  opacity: 0;
  animation: ${fadeInUp} 0.6s ease forwards;
  animation-delay: ${({ delay }) => delay}s;
  transition: all 0.4s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 16px 36px rgba(45, 31, 24, 0.05);
    border-color: rgba(197, 168, 128, 0.35);
  }
`;

const BlogThumbnail = styled.div`
  width: 100%;
  height: 200px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }

  ${BlogCard}:hover & img {
    transform: scale(1.04);
  }
`;

const BlogContent = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 12px;
`;

const CardMeta = styled.div`
  display: flex;
  font-family: "Jost", sans-serif;
  font-size: 12px;
  color: #C5A880;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const BlogTitle = styled.h3`
  margin: 0;
  font-family: "Playfair Display", serif;
  font-size: 19px;
  font-weight: 700;
  line-height: 1.35;

  a {
    color: #123524;
    transition: color 0.3s;

    &:hover {
      color: #C5A880;
    }
  }
`;

const CardExcerpt = styled.p`
  margin: 0;
  font-family: "Poppins", sans-serif;
  color: #2D1F18;
  opacity: 0.8;
  font-size: 13.5px;
  line-height: 1.55;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const BlogFooter = styled.div`
  margin-top: auto;
  padding-top: 12px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const Sidebar = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const SidebarCard = styled.div`
  background: #ffffff;
  border-radius: 24px;
  padding: 28px;
  border: 1px solid rgba(197, 168, 128, 0.2);
  box-shadow: 0 8px 24px rgba(45, 31, 24, 0.015);
  display: flex;
  flex-direction: column;
  gap: 20px;
  animation: ${fadeInRight} 0.6s ease forwards;
`;

const SidebarHeading = styled.h4`
  margin: 0;
  font-family: "Playfair Display", serif;
  font-size: 18px;
  font-weight: 700;
  color: #123524;
  position: relative;
  padding-bottom: 8px;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 32px;
    height: 2px;
    background: #C5A880;
  }
`;

const CategoryList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CategoryItem = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-radius: 12px;
  background: #FDFBF7;
  border: 1px solid rgba(197, 168, 128, 0.12);
  transition: all 0.3s ease;
  transform: translateY(15px);
  opacity: 0;
  animation: ${fadeInUp} 0.6s ease forwards;
  animation-delay: ${({ delay }) => delay}s;

  &:hover {
    background: #F2ECE1;
    transform: translateY(-2px);
    border-color: rgba(197, 168, 128, 0.3);
  }

  a {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    gap: 12px;
    color: #2D1F18;
    font-family: "Jost", sans-serif;
    font-weight: 500;
    font-size: 14px;

    .category-info {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    img {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      object-fit: cover;
      border: 1px solid rgba(197, 168, 128, 0.25);
    }
  }
`;

const Badge = styled.span`
  min-width: 26px;
  height: 26px;
  padding: 0 4px;
  border-radius: 13px;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: "Jost", sans-serif;
  font-size: 11px;
  font-weight: 600;
  color: #123524;
  border: 1px solid rgba(197, 168, 128, 0.2);
  box-shadow: 0 2px 6px rgba(45, 31, 24, 0.04);
`;

const TagList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const TagItem = styled.div`
  transform: translateY(10px);
  opacity: 0;
  animation: ${fadeInUp} 0.5s ease forwards;
  animation-delay: ${({ delay }) => delay}s;
`;

const TagButton = styled.button`
  border: 1px solid ${({ active }) => (active ? "#123524" : "rgba(197, 168, 128, 0.35)")};
  border-radius: 20px;
  padding: 6px 14px;
  background: ${({ active }) => (active ? "#123524" : "#ffffff")};
  color: ${({ active }) => (active ? "#FDFBF7" : "#2D1F18")};
  font-family: "Jost", sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #123524;
    background: ${({ active }) => (active ? "#123524" : "#F2ECE1")};
    color: ${({ active }) => (active ? "#FDFBF7" : "#123524")};
  }
`;
